package com.schoolconnect.app.feature.schoolusers

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogColors
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.feature.common.ApiSchoolDropdown
import com.schoolconnect.app.feature.common.toDateComparable
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import com.schoolconnect.app.feature.schoolusers.add.AddSchoolUserSchoolOption
import com.schoolconnect.app.feature.schoolusers.add.addSchoolUserRoles
import com.schoolconnect.app.feature.schoolusers.add.addSchoolUserSchools
import com.schoolconnect.app.feature.schoolusers.add.addSchoolUserStatuses
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.filter
import ischoolconnect.sharedui.generated.resources.search
import org.jetbrains.compose.resources.painterResource

private val FilterUsersOverlay = Color(0x730F172A)
private val FilterUsersPrimary = Color(0xFF2563EB)
private val FilterUsersPrimaryHover = Color(0xFF084BE0)
private val FilterUsersNavy = Color(0xFF111827)
private val FilterUsersMuted = Color(0xFF536080)
private val FilterUsersBorder = Color(0xFFE5E7EB)
private val FilterUsersInputBorder = Color(0xFFE5E7EB)
private val FilterUsersSurface = Color.White
private val FilterUsersSelectedBg = Color(0xFFEEF5FF)
private val FilterUsersError = Color(0xFFC62828)
private val FilterUsersDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors


@Composable
internal fun SchoolUsersFilterModal(
    open: Boolean,
    initialValues: SchoolUsersFilterFormValues,
    onClose: () -> Unit,
    onApply: (SchoolUsersFilterFormValues) -> Unit,
    schoolOptions: List<AddSchoolUserSchoolOption> = addSchoolUserSchools,
    roleOptions: List<String> = addSchoolUserRoles,
    statusOptions: List<String> = addSchoolUserStatuses
) {
    if (!open) return

    var schoolId by remember(initialValues) { mutableStateOf(initialValues.schoolId) }
    var role by remember(initialValues) { mutableStateOf(initialValues.role) }
    var status by remember(initialValues) { mutableStateOf(initialValues.status) }
    var createdFrom by remember(initialValues) { mutableStateOf(initialValues.createdFrom) }
    var createdTo by remember(initialValues) { mutableStateOf(initialValues.createdTo) }
    var schoolLabel by remember(initialValues) { mutableStateOf("") }
    var schoolQuery by remember(initialValues) { mutableStateOf("") }
    var schoolExpanded by remember { mutableStateOf(false) }
    var roleExpanded by remember { mutableStateOf(false) }
    var statusExpanded by remember { mutableStateOf(false) }

    val filteredSchools = remember(schoolQuery, schoolOptions) {
        val query = schoolQuery.trim()
        if (query.isBlank()) {
            schoolOptions
        } else {
            schoolOptions.filter { it.name.contains(query, ignoreCase = true) }
        }
    }

    LaunchedEffect(open, initialValues) {
        if (open) {
            val initialSchoolLabel = initialValues.schoolName.ifBlank {
                schoolOptions.firstOrNull { it.id == initialValues.schoolId }?.name.orEmpty()
            }
            if (initialSchoolLabel.isNotBlank() || initialValues.schoolId != schoolId) {
                schoolLabel = initialSchoolLabel
            }
            schoolId = initialValues.schoolId
            role = initialValues.role
            status = initialValues.status
            createdFrom = initialValues.createdFrom
            createdTo = initialValues.createdTo
            schoolQuery = ""
            schoolExpanded = false
            roleExpanded = false
            statusExpanded = false
        }
    }

    AppDialogScaffold(
        onDismiss = onClose,
        size = AppDialogSize.Standard,
        colors = FilterUsersDialogColors,
        maxDialogHeight = 720.dp
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            FilterUsersHeader(onClose = onClose)
            AppDialogDivider(colors = FilterUsersDialogColors)

            val scrollState = rememberScrollState()
            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f, fill = false)
            ) {
                val compact = maxWidth < 560.dp
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(scrollState)
                        .padding(horizontal = 24.dp, vertical = 24.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    val schoolField: @Composable (Modifier) -> Unit = { fieldModifier ->
                        FilterUsersFieldSection(
                            modifier = fieldModifier,
                            label = "School"
                        ) {
                            FilterUsersSchoolSelect(
                                value = schoolLabel,
                                query = schoolQuery,
                                expanded = schoolExpanded,
                                filteredSchools = filteredSchools,
                                onQueryChange = { schoolQuery = it },
                                onToggleExpanded = { schoolExpanded = !schoolExpanded },
                                onDismissExpanded = { schoolExpanded = false },
                                onSchoolSelected = {
                                    schoolId = it.id
                                    schoolLabel = it.name
                                    schoolQuery = it.name
                                }
                            )
                        }
                    }

                    val roleField: @Composable (Modifier) -> Unit = { fieldModifier ->
                        FilterUsersFieldSection(
                            modifier = fieldModifier,
                            label = "Role"
                        ) {
                            FilterUsersSelect(
                                value = role.ifBlank { "All" },
                                placeholder = "All",
                                options = listOf("All") + roleOptions,
                                expanded = roleExpanded,
                                onToggleExpanded = { roleExpanded = !roleExpanded },
                                onDismissExpanded = { roleExpanded = false },
                                onSelected = { role = if (it == "All") "" else it }
                            )
                        }
                    }

                    val statusField: @Composable (Modifier) -> Unit = { fieldModifier ->
                        FilterUsersFieldSection(
                            modifier = fieldModifier,
                            label = "Status"
                        ) {
                            FilterUsersSelect(
                                value = status.ifBlank { "All" },
                                placeholder = "All",
                                options = listOf("All") + statusOptions,
                                expanded = statusExpanded,
                                onToggleExpanded = { statusExpanded = !statusExpanded },
                                onDismissExpanded = { statusExpanded = false },
                                onSelected = { status = if (it == "All") "" else it }
                            )
                        }
                    }

                    if (compact) {
                        schoolField(Modifier.fillMaxWidth())
                        roleField(Modifier.fillMaxWidth())
                        statusField(Modifier.fillMaxWidth())
                    } else {
                        schoolField(Modifier.fillMaxWidth(0.5f))
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            verticalAlignment = Alignment.Top
                        ) {
                            roleField(Modifier.weight(1f))
                            statusField(Modifier.weight(1f))
                        }
                    }

                    FilterUsersDateSection(
                        compact = compact,
                        createdFrom = createdFrom,
                        createdTo = createdTo,
                        onCreatedFromChange = { createdFrom = it },
                        onCreatedToChange = { createdTo = it }
                    )

                    val validDateRange = isValidDateRange(createdFrom, createdTo)
                    if (!validDateRange) {
                        Text(
                            text = "From date must be before To date.",
                            color = FilterUsersError,
                            fontSize = 14.sp,
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
            }

            FilterUsersFooter(
                onCancel = onClose,
                onReset = {
                    schoolId = ""
                    schoolLabel = ""
                    role = ""
                    status = ""
                    createdFrom = ""
                    createdTo = ""
                    schoolQuery = ""
                    schoolExpanded = false
                    roleExpanded = false
                    statusExpanded = false
                },
                onApply = {
                    val values = SchoolUsersFilterFormValues(
                        searchQuery = initialValues.searchQuery,
                        schoolId = schoolId.trim(),
                        schoolName = schoolLabel.trim(),
                        role = role.trim(),
                        status = status.trim(),
                        createdFrom = createdFrom.trim(),
                        createdTo = createdTo.trim()
                    )
                    if (!isValidDateRange(values.createdFrom, values.createdTo)) {
                    } else {
                        onApply(values)
                    }
                }
            )
        }
    }
}

@Composable
private fun FilterUsersHeader(
    onClose: () -> Unit
) {
    AppDialogHeader(
        title = "Filter Users",
        subtitle = "Narrow down your user list",
        icon = Res.drawable.filter,
        closeContentDescription = "Close filter users dialog",
        onClose = onClose,
        colors = FilterUsersDialogColors
    )
}

@Composable
private fun FilterUsersFieldSection(
    modifier: Modifier = Modifier,
    label: String,
    field: @Composable () -> Unit
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(6.dp)
    ) {
        Text(
            text = label,
            color = FilterUsersNavy,
            fontSize = 13.sp,
            fontWeight = FontWeight.SemiBold
        )
        field()
    }
}

@Composable
private fun FilterUsersSelect(
    value: String,
    placeholder: String,
    options: List<String>,
    expanded: Boolean,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSelected: (String) -> Unit
) {
    AppDialogSelectField(
        value = value,
        placeholder = placeholder,
        expanded = expanded,
        options = options,
        onToggleExpanded = onToggleExpanded,
        onDismissExpanded = onDismissExpanded,
        onSelected = onSelected,
        colors = FilterUsersDialogColors,
        fieldHeight = 40.dp,
        fontSize = 14.sp
    )
}

@Composable
private fun FilterUsersSchoolSelect(
    value: String,
    query: String,
    expanded: Boolean,
    filteredSchools: List<AddSchoolUserSchoolOption>,
    onQueryChange: (String) -> Unit,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSchoolSelected: (AddSchoolUserSchoolOption) -> Unit
) {
    ApiSchoolDropdown(
        selectedLabel = value,
        placeholder = "Search and select school",
        onSelected = { option ->
            onSchoolSelected(AddSchoolUserSchoolOption(id = option.id, name = option.name))
            onDismissExpanded()
        }
    )
    return

    var anchorSize by remember { mutableStateOf(IntSize.Zero) }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .onGloballyPositioned { anchorSize = it.size }
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .height(54.dp),
            shape = RoundedCornerShape(10.dp),
            color = FilterUsersSurface,
            border = androidx.compose.foundation.BorderStroke(1.dp, FilterUsersInputBorder),
            onClick = onToggleExpanded
        ) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 14.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = value.ifBlank { "Search and select school" },
                    color = if (value.isBlank()) FilterUsersMuted else FilterUsersNavy,
                    style = TextStyle(fontSize = 15.sp, fontWeight = FontWeight.Medium),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Icon(
                    painter = painterResource(Res.drawable.chevron_down),
                    contentDescription = null,
                    tint = FilterUsersNavy,
                    modifier = Modifier.size(15.dp)
                )
            }
        }

        if (expanded) {
            Popup(
                popupPositionProvider = anchoredPopupPositionProvider(),
                properties = PopupProperties(
                    focusable = true,
                    dismissOnBackPress = true,
                    dismissOnClickOutside = true
                ),
                onDismissRequest = onDismissExpanded
            ) {
                Surface(
                    modifier = Modifier.width(with(LocalDensity.current) { anchorSize.width.toDp() }),
                    shape = RoundedCornerShape(12.dp),
                    color = FilterUsersSurface,
                    shadowElevation = 16.dp,
                    border = androidx.compose.foundation.BorderStroke(1.dp, FilterUsersBorder)
                ) {
                    Column {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(40.dp)
                                .padding(horizontal = 12.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(6.dp)
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.search),
                                contentDescription = null,
                                tint = FilterUsersMuted,
                                modifier = Modifier.size(14.dp)
                            )
                            BasicTextField(
                                value = query,
                                onValueChange = onQueryChange,
                                singleLine = true,
                                textStyle = TextStyle(
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.Medium,
                                    color = FilterUsersNavy
                                ),
                                cursorBrush = SolidColor(com.schoolconnect.app.designsystem.AppPrimary),
                                modifier = Modifier.fillMaxWidth()
                            ) { innerTextField ->
                                Box(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    if (query.isBlank()) {
                                        Text(
                                            text = "Search school...",
                                            color = FilterUsersMuted,
                                            fontSize = 14.sp,
                                            fontWeight = FontWeight.Medium
                                        )
                                    }
                                    innerTextField()
                                }
                            }
                        }

                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(max = 160.dp)
                                .verticalScroll(rememberScrollState())
                        ) {
                            if (filteredSchools.isEmpty()) {
                                Text(
                                    text = "No schools found",
                                    color = FilterUsersMuted,
                                        fontSize = 12.sp,
                                        fontWeight = FontWeight.Medium,
                                        modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp)
                                    )
                                } else {
                                filteredSchools.forEach { option ->
                                    val selected = option.name == value
                                    Box(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                                .height(34.dp)
                                            .background(if (selected) FilterUsersSelectedBg else Color.Transparent)
                                            .clickable {
                                                onSchoolSelected(option)
                                                onDismissExpanded()
                                            }
                                            .padding(horizontal = 18.dp),
                                        contentAlignment = Alignment.CenterStart
                                    ) {
                                        Text(
                                            text = option.name,
                                            color = FilterUsersNavy,
                                            fontSize = 14.sp,
                                            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
                                            maxLines = 1,
                                            overflow = TextOverflow.Ellipsis
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FilterUsersDateSection(
    compact: Boolean,
    createdFrom: String,
    createdTo: String,
    onCreatedFromChange: (String) -> Unit,
    onCreatedToChange: (String) -> Unit
) {
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            FilterUsersDateFieldBlock(
                label = "Created from",
                value = createdFrom,
                onValueChange = onCreatedFromChange
            )
            FilterUsersDateFieldBlock(
                label = "Created to",
                value = createdTo,
                onValueChange = onCreatedToChange
            )
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
            FilterUsersDateFieldBlock(
                modifier = Modifier.weight(1f),
                label = "Created from",
                value = createdFrom,
                onValueChange = onCreatedFromChange
            )
            FilterUsersDateFieldBlock(
                modifier = Modifier.weight(1f),
                label = "Created to",
                value = createdTo,
                onValueChange = onCreatedToChange
            )
        }
    }
}

@Composable
private fun FilterUsersDateFieldBlock(
    modifier: Modifier = Modifier,
    label: String,
    value: String,
    onValueChange: (String) -> Unit
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        Text(
            text = label,
            color = FilterUsersNavy,
            fontSize = 13.sp,
            fontWeight = FontWeight.SemiBold
        )
        AppDialogDateField(
            value = value,
            onValueChange = onValueChange,
            placeholder = "DD MMM YYYY",
            colors = FilterUsersDialogColors
        )
    }
}

@Composable
private fun FilterUsersFooter(
    onCancel: () -> Unit,
    onReset: () -> Unit,
    onApply: () -> Unit
) {
    AppDialogFooter(
        actions = listOf(
            AppDialogFooterAction(label = "Cancel", onClick = onCancel),
            AppDialogFooterAction(label = "Reset", onClick = onReset),
            AppDialogFooterAction(label = "Apply", onClick = onApply, style = AppDialogActionStyle.Primary)
        ),
        colors = FilterUsersDialogColors,
        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 24.dp)
    )
}

private fun anchoredPopupPositionProvider(): PopupPositionProvider {
    return object : PopupPositionProvider {
        override fun calculatePosition(
            anchorBounds: IntRect,
            windowSize: IntSize,
            layoutDirection: androidx.compose.ui.unit.LayoutDirection,
            popupContentSize: IntSize
        ): IntOffset {
            val x = anchorBounds.left.coerceIn(0, (windowSize.width - popupContentSize.width).coerceAtLeast(0))
            val belowY = anchorBounds.bottom + 10
            val aboveY = anchorBounds.top - popupContentSize.height - 10
            val y = if (belowY + popupContentSize.height <= windowSize.height) belowY else aboveY.coerceAtLeast(10)
            return IntOffset(x, y)
        }
    }
}

private fun isValidDateRange(from: String, to: String): Boolean {
    val fromComparable = toDateComparable(from)
    val toComparable = toDateComparable(to)
    return when {
        from.isBlank() || to.isBlank() -> true
        fromComparable == null || toComparable == null -> false
        else -> fromComparable <= toComparable
    }
}
